import { useQuery } from "react-query";
import { toast } from "sonner";
import {
    _api_get_pilotage_absenteisme,
    _api_get_pilotage_diversite,
    _api_get_pilotage_effectif,
    _api_get_pilotage_frais,
    _api_get_pilotage_mouvements,
    _api_get_pilotage_retention,
    _api_get_pilotage_taches,
    _api_get_pilotage_temps_travail,
} from ".";
import {
    QUERY_KEY_PILOTAGE_ABSENTEISME,
    QUERY_KEY_PILOTAGE_DIVERSITE,
    QUERY_KEY_PILOTAGE_EFFECTIF,
    QUERY_KEY_PILOTAGE_FRAIS,
    QUERY_KEY_PILOTAGE_MOUVEMENTS,
    QUERY_KEY_PILOTAGE_RETENTION,
    QUERY_KEY_PILOTAGE_TACHES,
    QUERY_KEY_PILOTAGE_TEMPS_TRAVAIL,
} from "./keys";

/** Construit la query string commune aux endpoints /hr/pilotage/* */
export const buildPilotageSearchParams = (
    filters,
    { withPeriod = true } = {}
) => {
    const params = new URLSearchParams();
    if (withPeriod) params.set("period", filters.period);
    if (filters.agency_id) params.append("agency_ids", filters.agency_id);
    if (filters.manager_id) params.append("manager_ids", filters.manager_id);
    // department_ids : pas encore d'endpoint pour récupérer les ids de départements
    return params.toString();
};

/** Query générique pour une section de pilotage */
const usePilotageQuery = ({
    key,
    apiFn,
    filters,
    errorMessage,
    withPeriod = true,
}) => {
    const searchParams = buildPilotageSearchParams(filters, { withPeriod });

    const { data, isLoading, isFetching, isError } = useQuery(
        [key, searchParams],
        () => apiFn(searchParams),
        {
            refetchOnWindowFocus: false,
            keepPreviousData: true,
            onSuccess: (result) => {
                // fetcher ne throw pas sur 400 : l'erreur est dans le body
                if (result?.error) {
                    toast.error(result?.message || errorMessage);
                }
            },
        }
    );

    return {
        data: data?.error ? null : data,
        isLoading,
        isFetching,
        isError: isError || !!data?.error,
    };
};

export const useQueryPilotageEffectif = (filters) =>
    usePilotageQuery({
        key: QUERY_KEY_PILOTAGE_EFFECTIF,
        apiFn: _api_get_pilotage_effectif,
        filters,
        errorMessage: "Impossible de charger les données d'effectif",
    });

export const useQueryPilotageDiversite = (filters) =>
    usePilotageQuery({
        key: QUERY_KEY_PILOTAGE_DIVERSITE,
        apiFn: _api_get_pilotage_diversite,
        filters,
        errorMessage: "Impossible de charger les données de diversité",
    });

export const useQueryPilotageRetention = (filters) =>
    usePilotageQuery({
        key: QUERY_KEY_PILOTAGE_RETENTION,
        apiFn: _api_get_pilotage_retention,
        filters,
        errorMessage: "Impossible de charger les données de rétention",
    });

export const useQueryPilotageMouvements = (filters) =>
    usePilotageQuery({
        key: QUERY_KEY_PILOTAGE_MOUVEMENTS,
        apiFn: _api_get_pilotage_mouvements,
        filters,
        errorMessage: "Impossible de charger les données de mouvements",
    });

export const useQueryPilotageAbsenteisme = (filters) =>
    usePilotageQuery({
        key: QUERY_KEY_PILOTAGE_ABSENTEISME,
        apiFn: _api_get_pilotage_absenteisme,
        filters,
        errorMessage: "Impossible de charger les données d'absentéisme",
    });

/* Pas de param `period` sur cet endpoint */
export const useQueryPilotageTempsTravail = (filters) =>
    usePilotageQuery({
        key: QUERY_KEY_PILOTAGE_TEMPS_TRAVAIL,
        apiFn: _api_get_pilotage_temps_travail,
        filters,
        withPeriod: false,
        errorMessage: "Impossible de charger les données de temps de travail",
    });

export const useQueryPilotageFrais = (filters) =>
    usePilotageQuery({
        key: QUERY_KEY_PILOTAGE_FRAIS,
        apiFn: _api_get_pilotage_frais,
        filters,
        errorMessage: "Impossible de charger les données de notes de frais",
    });

export const useQueryPilotageTaches = (filters) =>
    usePilotageQuery({
        key: QUERY_KEY_PILOTAGE_TACHES,
        apiFn: _api_get_pilotage_taches,
        filters,
        errorMessage: "Impossible de charger les données de tâches & suivi",
    });
